import { createStore, combineReducers, applyMiddleware, Action, AnyAction } from "redux";
import { History } from "history";
import thunk from "redux-thunk";
import { CallHistoryMethodAction, connectRouter, routerMiddleware } from "connected-react-router";
import { composeWithDevTools } from "redux-devtools-extension";
import { toErrorPayload } from "../../helpers/errorPayload";
import { COMPLETED, CompleteAction, createCompleteAction, SET_SERVER_ERROR } from "../actions";

import * as DistanceNo from "../nissan_no/DistanceNo";
import * as Footer from "../Footer";
import * as Profile from "../Profile";
import * as Campaign from "../Campaign";
import * as DealerPrice from "../DealerPrice";
import * as Usp from "../USP";
import * as YoungDriver from "../YoungDriver";
import * as ImportedUsed from "../ImportedUsed";
import * as DamageFreeYear from "../DamageFreeYear";
import * as Insurance from "../Insurance";
import * as FinancingType from "./FinancingType";
import * as PaymentType from "../PaymentType";
import * as ComponentTexts from "../ComponentTexts";
import * as SingleRecomendation from "../SingleRecommendation";
import * as Bonus from "./Bonus";
import * as Vehicle from "./Vehicle";
import * as AddOn from "../AddOn";

import QuoteTask, { getQuotePayload } from "../fetch/nissan_no/quote";
import PriceTask, { getPayload } from "../fetch/nissan_no/calculate";

import {
  RECEIVE_PRICE,
  REQUEST_PRICE,
  RECEIVE_QUOTE,
  REQUEST_QUOTE,
  PROCESSING_START,
  PROCESSING_END,
  ProcessingAction,
  createStartProcessingAction,
  createStopProcessingAction,
} from "../actions";

import { distanceNoReducer, footerReducer, profileReducer } from "./actions";
import { errorReducer } from "../error";
import { push } from "connected-react-router";
import Purchase from "../fetch/nissan_no/purchase";
import { GDPR_CHECK } from "../Profile";
import { IAddOnActionData } from "../AddOn";
import { datalayerMiddlewareNo } from "../../analytics";
import { BrandsEnum } from "../../enums";
import putChassisNumberToQuote from "../fetch/nissan_no/putChassisNumberToQuote";

export const CHECKOUT_ROUTE = "/checkout";
export const DONE_ROUTE = "/done";
export const BASE_ROUTE = "/";

// Helper to safely extract error payload

export interface ApplicationState {
  campaign: Campaign.CampaignState | undefined;
  dealerPrice: DealerPrice.DealerPriceState | undefined;
  usp: Usp.USPState | undefined;
  distance: DistanceNo.DistanceNoState | undefined;
  footer: Footer.FooterState | undefined;
  profile: Profile.ProfileState | undefined;
  addon: AddOn.AddOnState | undefined;
  youngDriver: YoungDriver.YoungDriverState | undefined;
  importedUsed: ImportedUsed.ImportedUsedState | undefined;
  damageFreeYear: DamageFreeYear.DamageFreeYearState | undefined;
  insurance: Insurance.InsuranceState | undefined;
  financingOptions: FinancingType.FinancingTypesState | undefined;
  texts: ComponentTexts.Texts | undefined;
  bonus: Bonus.BonusState | undefined;
  singleRecomendation: SingleRecomendation.SingleRecommendationState | undefined;
  vehicle: Vehicle.VehicleState | undefined;
  ui: UI;
  inputs: {
    apiHost: string;
    cmsHost: string;
    partner: string;
    socialSecurityNumberNo?: string;
    quoteId?: string;
    firstName?: string;
    lastName?: string;
    isCarModel?: boolean;
    carModel?: string;
    vehicle?: {
      modelYear?: number;
      make?: string;
      model?: string;
      vehicleIdentity?: {
        registrationNumberNo?: string;
      };
      modelIdentity?: {
        modelIdentifier?: string;
        firstRegistrationYear?: number;
      };
    };
    errorUrl: string;
    decoupledRendering: {
      mainLogo: string;
      mainLogoAlt: string;
      secondaryLogo: string;
      secondaryLogoAlt: string;
      isDecoupledRendering: boolean;
    };
  };
  payment?: {
    paymentFrequencyAndMethodNo?: string;
    paymentAgreementCreationInstructions?: {
      instructionsNo?: string;
      link?: string;
    };
    startDate?: Date;
  };
  payload?: string;
}

export interface UI {
  initialized: boolean;
  completed: boolean;
  processing: boolean;
  serverError?: {
    message: string;
    header?: string;
    timestamp: number;
  };
}

export const reducers = {
  campaign: Campaign.reducer,
  dealerPrice: DealerPrice.reducer,
  usp: Usp.reducer,
  distance: distanceNoReducer,
  payment: PaymentType.reducer,
  financingOptions: FinancingType.reducer,
  footer: footerReducer,
  profile: profileReducer,
  addon: AddOn.reducer,
  singleRecomendation: SingleRecomendation.reducer,
  youngDriver: YoungDriver.reducer,
  importedUsed: ImportedUsed.reducer,
  damageFreeYear: DamageFreeYear.reducer,
  insurance: Insurance.reducer,
  texts: ComponentTexts.reducer,
  bonus: Bonus.reducer,
  vehicle: Vehicle.reducer,
  inputs: (state = {}) => state,
  payload: (state = null) => state,
  ui: (
    state: UI = {
      initialized: false,
      completed: false,
      processing: false,
    },
    action: Action,
  ) => {
    switch (action.type) {
      case COMPLETED:
        return { ...state, completed: true };
      case PROCESSING_START:
        return { ...state, processing: true };
      case PROCESSING_END:
        return { ...state, processing: false };
      // Removed SET_SERVER_ERROR from Nissan NO ui reducer. All error state is now handled by the global error reducer.
      // Removed RESET_SERVER_ERROR from Nissan NO ui reducer. All error state is now handled by the global error reducer.
      default:
        return state;
    }
  },
  decoupledRendering: (state = null) => state,
};

let store: any;

export function setupStore(history: History, initialState?: ApplicationState) {
  const root = (state: any, action: AnyAction | ReceiveAction) => {
    const initial = action as ReceiveAction;
    if (initial && initial.type === RECEIVE_QUOTE) {
      const { ui, router } = state;
      const bonus = initial.payload?.bonus;
      const youngDriver = initial.payload?.youngDriver;
      const importedUsed = initial.payload?.importedUsed;
      const addon = initial.payload?.addon;
      const insurance = initial.payload?.insurance;
      const distance = initial.payload?.distance;
      const footer = initial.payload?.footer;
      const profile = initial.payload?.profile;
      const texts = initial.payload?.texts;
      const inputs = initial.payload?.inputs ?? state.inputs;
      const vehicle = initial.payload?.vehicle;
      const payload = initial.payload?.payload;
      const financingOptions = initial.payload?.financingOptions;
      state = {
        ui: {
          ...ui,
          initialized: true,
        },
        inputs,
        router,
        bonus,
        youngDriver,
        importedUsed,
        addon,
        insurance,
        distance,
        footer,
        profile,
        texts,
        vehicle,
        payload,
        financingOptions,
      };
    }
    if (initial && initial.type === RECEIVE_PRICE) {
      const footer = initial.payload?.footer;
      const addon = initial.payload?.addon;
      const insurance = initial.payload?.insurance;
      const payload = initial.payload?.payload;
      state = { ...state, insurance, addon, footer, payload };
    }
    if (initial && initial.type === CREATED) {
      const payment = initial.payload?.payment;
      state = { ...state, payment };
    }
    return rootReducer(state, action);
  };

  const rootReducer = combineReducers({
    ...reducers,
    error: errorReducer,
    router: connectRouter(history),
  });

  const middleware = [thunk, routerMiddleware(history), datalayerMiddlewareNo];

  const enhancers: any[] = [];

  store = createStore(
    root,
    {
      // default
      ui: initialState?.ui,
      inputs: initialState?.inputs,
    },
    // used instead of compose() to allow easier store debugging @ browsers Redux DevTools extension
    composeWithDevTools(applyMiddleware(...middleware), ...enhancers),
  );
  return store;
}

export interface AppThunkAction<TAction> {
  (dispatch: (action: TAction) => void, getState: () => ApplicationState): void;
}

export const CREATED = "CREATED";
export const VEHICLE_IS_VALID = "VEHICLE_IS_VALID";

export type VehicleIsValidAction = { type: typeof VEHICLE_IS_VALID };

export const MODEL_IS_VALID = "MODEL_IS_VALID";

export type ModelIsValidAction = { type: typeof MODEL_IS_VALID };

export const COMPANY_IS_VALID = "COMPANY_IS_VALID";
export type CompanyIsValidAction = { type: typeof COMPANY_IS_VALID };

export type GDPRCheckAction = { type: typeof GDPR_CHECK };

export interface ServerErrorPayload {
  message: string;
  header?: string;
  timestamp: number;
}

export interface SetServerErrorAction {
  type: typeof SET_SERVER_ERROR;
  payload: ServerErrorPayload;
}

export interface ReceiveAction {
  type: typeof RECEIVE_QUOTE | typeof RECEIVE_PRICE | typeof CREATED;
  payload: any;
}

export interface PriceRequestAction {
  type: typeof REQUEST_PRICE;
}

export interface QuoteRequestAction {
  type: typeof REQUEST_QUOTE;
}

export type QuoteActions = QuoteRequestAction | ReceiveAction;
export type PriceActions = PriceRequestAction | ReceiveAction;

export const remoteNOActions = {
  requestQuote:
    (): AppThunkAction<QuoteActions | ProcessingAction | SetServerErrorAction | CallHistoryMethodAction> => async (dispatch, getState) => {
      const state = getState();

      if (state?.payload === getQuotePayload(state)) {
        return;
      }

      dispatch(createStartProcessingAction());
      dispatch({
        type: REQUEST_QUOTE,
      });
      try {
        const appState = await QuoteTask(getState());
        dispatch({
          type: RECEIVE_QUOTE,
          payload: appState,
        });
      } catch (ex) {
        dispatch({
          type: SET_SERVER_ERROR,
          payload: toErrorPayload(ex),
        });
      } finally {
        dispatch(createStopProcessingAction());
      }
    },
  requestPriceData:
    (
      pushPath?: string,
    ): AppThunkAction<PriceActions | ProcessingAction | SetServerErrorAction | CallHistoryMethodAction | FinancingType.FinanceAction> =>
    async (dispatch, getState) => {
      const state = getState();
      const isYoungDriverValid = state.youngDriver?.questionVisible && (state.youngDriver?.selected !== null ? true : false);
      const isImportedUsedValid = state.importedUsed?.questionVisible ? (state.importedUsed?.selected !== null ? true : false) : true;
      const isDistanceValid = state.distance?.selectedId ? true : false;
      const isValid = state?.ui?.initialized && isYoungDriverValid && state.bonus?.isValid && isImportedUsedValid && isDistanceValid;
      if (isValid) {
        if (state?.payload === getPayload(state)) {
          if (pushPath) {
            dispatch(push(pushPath));
          }
        }

        dispatch(createStartProcessingAction());
        dispatch({
          type: REQUEST_PRICE,
        });
        try {
          const appState = await PriceTask(getState());
          dispatch({
            type: RECEIVE_PRICE,
            payload: appState,
          });

          if (pushPath) {
            dispatch(push(pushPath));
          }
        } catch (ex) {
          dispatch({
            type: SET_SERVER_ERROR,
            payload: toErrorPayload(ex),
          });
        } finally {
          dispatch(createStopProcessingAction());
        }
      }
    },

  validateSelectedFinancingType: (): AppThunkAction<FinancingType.FinanceAction> => (dispatch, getState) => {
    const state = getState();
    if (FinancingType.validateSelectedFinanceType(state.financingOptions) === false) {
      dispatch({
        type: FinancingType.FINANCE_TYPE_INVALID,
        data: {
          financeTypeId: state.financingOptions?.selectedFinanceTypeId,
          financeCompanyId: state.financingOptions?.selectedCompanyId,
        },
      });
    } else {
      dispatch({
        type: FinancingType.FINANCE_TYPE_VALID,
        data: {
          financeTypeId: state.financingOptions?.selectedFinanceTypeId,
          financeCompanyId: state.financingOptions?.selectedCompanyId,
        },
      });
    }
  },
  validateSelectedFinancingCompany: (): AppThunkAction<FinancingType.FinanceAction> => (dispatch, getState) => {
    const state = getState();
    if (FinancingType.validateSelectedCompany(state.financingOptions) === false) {
      dispatch({
        type: FinancingType.FINANCE_COMPANY_INVALID,
        data: {
          financeTypeId: state.financingOptions?.selectedFinanceTypeId,
          financeCompanyId: state.financingOptions?.selectedCompanyId,
        },
      });
    } else {
      dispatch({
        type: FinancingType.FINANCE_COMPANY_VALID,
        data: {
          financeTypeId: state.financingOptions?.selectedFinanceTypeId,
          financeCompanyId: state.financingOptions?.selectedCompanyId,
        },
      });
    }
  },

  putChassisNumberToQuote:
    (): AppThunkAction<VehicleIsValidAction | ProcessingAction | SetServerErrorAction | CallHistoryMethodAction> =>
    async (dispatch, getState) => {
      const state = getState();
      if (state?.ui?.initialized && state.vehicle?.VINNo?.value && state.vehicle?.VINNo.state === 0) {
        try {
          dispatch(createStartProcessingAction());
          await putChassisNumberToQuote(state);
        } catch (ex) {
          dispatch({
            type: SET_SERVER_ERROR,
            payload: toErrorPayload(ex),
          });
        } finally {
          dispatch(createStopProcessingAction());
        }
      }
    },
  purchase:
    (): AppThunkAction<
      | ReceiveAction
      | CompleteAction
      | ProcessingAction
      | SetServerErrorAction
      | CallHistoryMethodAction
      | ModelIsValidAction
      | YoungDriver.YoungDriverAction
      | GDPRCheckAction
      | VehicleIsValidAction
      | CompanyIsValidAction
    > =>
    async (dispatch, getState) => {
      if (getState().ui.processing) {
        await new Promise<void>(resolve => {
          const loop = () => {
            setTimeout(() => {
              if (getState().ui.processing) {
                loop();
              } else {
                resolve();
              }
            }, 200);
          };
          if (getState().ui.processing) {
            loop();
          }
        });
      }

      /* protection */
      if (getState().ui.completed) {
        return;
      }

      dispatch({
        type: MODEL_IS_VALID,
      });

      dispatch({
        type: GDPR_CHECK,
      });

      dispatch({
        type: VEHICLE_IS_VALID,
      });

      dispatch({
        type: COMPANY_IS_VALID,
      });

      const state = getState();

      let vehicleError = false;
      if (state.inputs.isCarModel) {
        vehicleError = state.vehicle?.isValid ? false : true;
      }

      const actualMileageValid = state.profile?.actualMilage?.value !== "";
      const showErrorForBonusOriginSelection = state.bonus?.bonus?.bonusOriginNeeded && state.bonus.previousCompany.showError;

      const isValid =
        state?.ui?.initialized &&
        state.profile?.isValid &&
        actualMileageValid &&
        state.bonus?.isValid &&
        state.distance?.selectedId &&
        !showErrorForBonusOriginSelection &&
        !state.profile.gdpr?.showError &&
        !state.youngDriver?.showError &&
        !vehicleError;

      if (isValid) {
        try {
          dispatch(createStartProcessingAction());
          const appState = await Purchase(getState());
          dispatch({
            type: CREATED,
            payload: appState,
          });
          dispatch(createCompleteAction());
        } catch (ex) {
          dispatch({
            type: SET_SERVER_ERROR,
            payload: toErrorPayload(ex),
          });
        } finally {
          dispatch(createStopProcessingAction());
        }
      }
    },
  validateYoungDriver: (): AppThunkAction<YoungDriver.YoungDriverAction> => (dispatch, getState) => {
    const state = getState();
    if (state.youngDriver?.selected === null) {
      dispatch({
        type: YoungDriver.YOUNG_DRIVER_INVALID,
        payload: { value: state.youngDriver?.selected },
      });
    } else {
      dispatch({
        type: YoungDriver.YOUNG_DRIVER_VALID,
        payload: { value: state.youngDriver?.selected },
      });
    }
  },
  validateImportedUsed: (): AppThunkAction<ImportedUsed.ImportedUsedAction> => (dispatch, getState) => {
    const state = getState();
    if (state.importedUsed?.selected === null) {
      dispatch({
        type: ImportedUsed.IMPORTED_USED_INVALID,
        payload: { value: state.importedUsed?.selected },
      });
    } else {
      dispatch({
        type: ImportedUsed.IMPORTED_USED_VALID,
        payload: { value: state.importedUsed?.selected },
      });
    }
  },
  validateLowMilage: (): AppThunkAction<AddOn.AddOnAction> => (dispatch, getState) => {
    const state = getState();
    const partner = state.inputs.partner;
    let addOn: AddOn.IAddOn | undefined = undefined;
    if (partner === BrandsEnum.VolviaNo) {
      addOn = state.addon?.addOns?.find(x => x.Name.includes("Maskinskade"));
    } else if (partner === BrandsEnum.NissanNo) {
      addOn = state.addon?.addOns?.find(x => x.Name.includes("Motor"));
    }
    dispatch({
      type: AddOn.CHECK_LOW_MILAGE,
      data: {
        addOnInternalCode: addOn?.InternalCode,
        newState: addOn?.lowMillage?.selected,
      } as IAddOnActionData,
    });
  },
  validateBonus: (): AppThunkAction<Bonus.ChangeAction> => (dispatch, getState) => {
    const state = getState();
    if (state.bonus?.bonus?.selectedId === "0") {
      dispatch({
        type: Bonus.BONUS_INVALID,
        payload: { value: undefined },
      });
    } else {
      dispatch({
        type: Bonus.BONUS_VALID,
        payload: { value: undefined },
      });
    }
  },
};
